import { useCallback, useEffect } from "react";
import { FieldValues, UseFormReturn, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { DateField, MultipleSelect, RadioGroupField, TextAreaField } from "components/Form";
import {
  DARK_GRAY,
  translate,
  RecommendationResponse,
  SelectOption,
  usersAdOptionsSelector,
  usersThunks,
  formValidate,
  addDaysToDate,
  isDateExpired,
  getOptionsYesOrNoV2,
  AppDispatch,
  formatNativeDateInputValue,
  useCtoDateSelectionMaxDays,
} from "@shape/reef-shared";
import { BaseField } from "components/Form/BaseField";
import SelectWithFilter from "components/SelectWithFilter/SelectWithFilter";
import { CreateStep2Body, TagStatusType } from "types";
import styles from "./AssigningRecommendation.module.scss";

export type AssigningRecommendationProps<TFieldValues extends FieldValues> = Readonly<{
  form: UseFormReturn<TFieldValues>;
  initialValues: RecommendationResponse;
  readOnly: boolean;
  prefix: string;
  index: number;
  vesselId: string;
  isNewCto: boolean;
  tags: TagStatusType[];
  hasTagsSelectField: boolean;
  flaggedQuestions: boolean;
}>;

export function AssigningRecommendation({
  form,
  initialValues,
  readOnly,
  prefix,
  index,
  vesselId,
  isNewCto,
  tags,
  hasTagsSelectField,
  flaggedQuestions,
}: AssigningRecommendationProps<CreateStep2Body>) {
  const ctoDateSelectionMaxDays = useCtoDateSelectionMaxDays(vesselId);
  const tagOptions: SelectOption[] = tags.map((tag) => ({
    label: tag.tag_code,
    id: tag.tag_code,
  }));

  const radioProps = {
    form,
    name: prefix,
    options: getOptionsYesOrNoV2(),
    rules: formValidate<CreateStep2Body, keyof CreateStep2Body>(),
    layout: "horizontal" as "horizontal" | "vertical",
    readOnly,
  };

  const dispatch = useDispatch<AppDispatch>();
  const usersOptions = useSelector(usersAdOptionsSelector());

  useEffect(() => {
    Promise.resolve(dispatch(usersThunks.fetchUsersByQuery("", 1000))).catch(() => undefined);
  }, [dispatch]);

  const updateUsersByQuery = useCallback(
    (query: string) => {
      dispatch(usersThunks.fetchUsersByQuery(query));
    },
    [dispatch]
  );

  const permanentChange = useWatch({
    control: form.control,
    name: `${prefix}.permanent_change` as keyof CreateStep2Body,
  });
  const closedAtEnd = useWatch({
    control: form.control,
    name: `${prefix}.has_action_to_be_closed` as keyof CreateStep2Body,
  });
  const dueDateValue = useWatch({
    control: form.control,
    name: `${prefix}.due_date` as keyof CreateStep2Body,
  }) as CreateStep2Body["assigning_recommendations"][number]["due_date"];
  const isTemporaryChange = permanentChange === false;
  const wontBeClosedAtEnd = closedAtEnd === false;
  const beCloseAtEndKey = `${prefix}.has_action_to_be_closed` as keyof CreateStep2Body;

  const validateDueDateNotInPast = (value: unknown) => {
    if (!value) return true;
    if (!(value instanceof Date) && typeof value !== "string") return false;
    return !isDateExpired(value);
  };

  const validateTemporaryDueDateAfterDueDate = (value: unknown) => {
    if (!value) return true;
    if (!(value instanceof Date) && typeof value !== "string") return false;

    const normalizedTemporaryDueDate = formatNativeDateInputValue(value);
    const normalizedDueDate = formatNativeDateInputValue(dueDateValue);

    if (!normalizedTemporaryDueDate || !normalizedDueDate) return true;

    return (
      normalizedTemporaryDueDate >= normalizedDueDate ||
      translate("extendDueDate.reversionDueDateMustBeAfterImplementation")
    );
  };

  useEffect(() => {
    if (!isTemporaryChange) {
      form.setValue(`${prefix}.has_action_to_be_closed` as keyof CreateStep2Body, null);
    }
  }, [form, isTemporaryChange, prefix]);

  useEffect(() => {
    if (wontBeClosedAtEnd) {
      void form.trigger(`${prefix}.temporary_due_date` as keyof CreateStep2Body);
    }
  }, [dueDateValue, form, prefix, wontBeClosedAtEnd]);

  return (
    <div className={styles.recommendationsWrapper}>
      <div className={styles.row}>
        <div className={`${styles.content} ${styles.grow1}`}>
          <BaseField
            form={form}
            name={`${prefix}.user_id` as keyof CreateStep2Body}
            label={translate("ctoStep2.actioneeNameOrEmail")}
            rules={{ required: true }}
            defaultValue={initialValues?.recommended_user?.user_id}
            render={({ field }) => {
              const safeKey =
                typeof field.value === "object" && field.value !== null
                  ? JSON.stringify(field.value)
                  : String(field.value);
              return (
                <div key={`assessment_team-${safeKey}`}>
                  {!readOnly ? (
                    <SelectWithFilter
                      readOnly={readOnly}
                      onFilterChange={updateUsersByQuery}
                      options={usersOptions}
                      maxResults={100}
                      placeholder={translate("formGeneric.typeNameOrEmail")}
                      selectedValue={field.value as number | null | undefined}
                      onChange={field.onChange}
                      background={DARK_GRAY}
                    />
                  ) : (
                    <div>{initialValues.recommended_user?.name ?? "-"}</div>
                  )}
                </div>
              );
            }}
          />
        </div>

        <div className={`${styles.content} ${styles.grow1}`}>
          <DateField
            form={form}
            name={`${prefix}.due_date` as keyof CreateStep2Body}
            label={translate("ctoPage.riskMatrix.recommendationDue")}
            readOnly={readOnly}
            background={DARK_GRAY}
            rules={{ validate: validateDueDateNotInPast, required: true }}
            defaultValue={initialValues?.due_date || addDaysToDate(30)}
            min={addDaysToDate(0)}
            max={addDaysToDate(ctoDateSelectionMaxDays)}
          />
        </div>

        {hasTagsSelectField && (
          <div className={`${styles.content} ${styles.grow1}`}>
            <MultipleSelect
              form={form}
              readOnly={readOnly}
              name={`${prefix}.tags` as keyof CreateStep2Body}
              label={translate("tags")}
              options={tagOptions}
              defaultValue={initialValues?.tags}
              isListInsideSelect
            />
          </div>
        )}
      </div>

      <div className={styles.recommendationsBox}>
        <TextAreaField
          name={`${prefix}.recommendation` as keyof CreateStep2Body}
          label={`${translate("generic.recommendation")} ${index + 1}`}
          placeholder={translate("formGeneric.typeHereMandatory")}
          readOnly={readOnly}
          form={form}
          rules={{ required: true }}
          defaultValue={initialValues?.recommendation}
        />

        <div className={styles.row}>
          <RadioGroupField
            {...radioProps}
            name={`${prefix}.permanent_change` as keyof CreateStep2Body}
            label={translate("genericComponents.isPermanentChange")}
            defaultValue={initialValues?.permanent_change}
          />
        </div>

        {isNewCto && isTemporaryChange && (
          <div className={styles.row}>
            <div className={styles.dueDateBox}>
              <h5>{translate("ctoStep2.fillInformation")}</h5>
              <RadioGroupField
                {...radioProps}
                name={beCloseAtEndKey}
                label={translate("ctoStep2.beClosedAtEnd")}
                defaultValue={initialValues?.has_action_to_be_closed}
                rules={
                  isTemporaryChange
                    ? formValidate<CreateStep2Body, typeof beCloseAtEndKey>()
                    : { required: false }
                }
              />
              {wontBeClosedAtEnd && (
                <DateField
                  form={form}
                  name={`${prefix}.temporary_due_date` as keyof CreateStep2Body}
                  label={translate("ctoStep2.temporaryDueDate")}
                  readOnly={readOnly}
                  background={DARK_GRAY}
                  rules={
                    wontBeClosedAtEnd
                      ? {
                          required: true,
                          validate: validateTemporaryDueDateAfterDueDate,
                        }
                      : undefined
                  }
                  defaultValue={initialValues?.temporary_due_date ?? addDaysToDate(30)}
                  min={dueDateValue ?? addDaysToDate(0)}
                  max={addDaysToDate(90)}
                />
              )}
            </div>
          </div>
        )}

        {flaggedQuestions && (
          <>
            <div className={styles.row}>
              <RadioGroupField
                {...radioProps}
                name={`${prefix}.potential_consequences_changed` as keyof CreateStep2Body}
                label={translate("ctoStep2.changedByAdditionalRecommendations")}
                defaultValue={initialValues?.potential_consequences_changed}
              />
            </div>

            <div className={styles.row}>
              <RadioGroupField
                {...radioProps}
                name={`${prefix}.additional_mitigation_needed` as keyof CreateStep2Body}
                label={translate("ctoStep2.additionalMitigation")}
                defaultValue={initialValues?.additional_mitigation_needed}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
